import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, dataService } from './services/dataService';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Inventory } from './pages/Inventory';
import { CarDetails } from './pages/CarDetails';
import { RequestCar } from './pages/RequestCar';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { Admin } from './pages/Admin';
import { DatabaseSettings } from './pages/DatabaseSettings';
import { UnderConstruction } from './pages/UnderConstruction';

function AppContent() {
  const [underConstruction, setUnderConstruction] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    // Load initial settings and auth state
    const initializeApp = async () => {
      try {
        const settings = await dataService.getSiteSettings();
        if (isMounted) {
          setUnderConstruction(settings.underConstruction);
        }
      } catch (err) {
        console.error("Failed to load site settings in App:", err);
      }

      onAuthStateChanged(auth, async (user) => {
        if (!isMounted) return;
        if (user) {
          try {
            const staffRecord = user.email ? await dataService.getStaffMemberByEmail(user.email) : undefined;
            if (staffRecord) {
              setIsAuthorized(true);
            } else if (user.email === 'nassoroa@gmail.com' || user.email?.includes('admin')) {
              setIsAuthorized(true);
            } else {
              setIsAuthorized(false);
            }
          } catch (err) {
            console.error("Error verifying staff permission in App:", err);
            setIsAuthorized(false);
          }
        } else {
          setIsAuthorized(false);
        }
        if (isMounted) {
          setLoading(false);
        }
      });
    };

    initializeApp();

    // Listen to real-time administrative status toggles on the same browser session
    const handleSettingsUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ underConstruction: boolean }>;
      if (isMounted) {
        setUnderConstruction(customEvent.detail.underConstruction);
      }
    };

    window.addEventListener('site-settings-updated', handleSettingsUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener('site-settings-updated', handleSettingsUpdate);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-darker text-white flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center gap-6 animate-pulse">
          <div className="text-3xl font-heading font-extrabold uppercase tracking-[0.2em] text-primary">
            DAKAR <span className="text-accent">AUTO SALES</span>
          </div>
          <div className="h-1 w-24 bg-gradient-to-r from-primary via-accent to-danger rounded"></div>
        </div>
      </div>
    );
  }

  const isAtAdmin = location.pathname === '/admin' || location.pathname === '/database';

  if (underConstruction && !isAuthorized && !isAtAdmin) {
    return <UnderConstruction />;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/inventory" element={<Layout><Inventory /></Layout>} />
      <Route path="/inventory/:id" element={<Layout><CarDetails /></Layout>} />
      <Route path="/request" element={<Layout><RequestCar /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/admin" element={<Layout><Admin /></Layout>} />
      <Route path="/database" element={<Layout><DatabaseSettings /></Layout>} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
